import { createFileRoute } from "@tanstack/react-router";
import { MarketingPage } from "@/components/MarketingPage";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { buildSocialMeta, SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: buildSocialMeta({
      title: "FAQ — CoachQuick",
      description: "Answers to common questions about CoachQuick for sports coaches.",
      path: "/faq",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/faq` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FaqPage,
});

const faqs: { q: string; a: string }[] = [
  {
    q: "What is CoachQuick?",
    a: "CoachQuick is an all-in-one CRM built for independent sports coaches — it keeps your clients, lessons, drills, invoices, and automated session recaps in one place.",
  },
  {
    q: "Which sports does it support?",
    a: "It works for any 1-on-1 or small-group coach. It's tuned for pickleball, tennis, and golf out of the box, but the drill library and notes are sport-agnostic.",
  },
  {
    q: "How does the automated session recap work?",
    a: "After you log a session, CoachQuick drafts a friendly recap email to your client based on your notes and the drills you covered. You review, edit, and send.",
  },
  {
    q: "Do my clients need to create an account?",
    a: "No. Clients receive recap emails, homework, and invoices via secure links — no signup required on their end.",
  },
  {
    q: "How does Google Calendar sync work?",
    a: "Connect your Google account in Settings. CoachQuick pulls your upcoming Google Calendar events into the dashboard so you can jump straight from an appointment to logging the session. We only request read access to event metadata.",
  },
  {
    q: "Can I sell lesson packages or prepaid bundles?",
    a: "Yes. Create a template (e.g. a 10-lesson pack), sell it to a client, and CoachQuick burns one credit each time you log a session. Low-balance nudges and your cancellation policy are handled automatically.",
  },
  {
    q: "How does billing work?",
    a: "You can try CoachQuick free during beta. When paid plans launch, you'll be able to subscribe via Stripe and manage or cancel from the in-app billing page anytime.",
  },
  {
    q: "Can I export my data?",
    a: "Yes. Go to Settings → Export your data to download a ZIP of all your clients, sessions, summaries, invoices, payments, library, and locations as CSVs plus a JSON archive. Available anytime — even if your subscription is paused or canceled.",
  },
  {
    q: "How do I get help or share feedback?",
    a: "Use our contact form or the Help & feedback link in the in-app menu. We read every message.",
  },
];

function FaqPage() {
  return (
    <MarketingPage>
      <div className="space-y-8">
        <header className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Frequently asked questions</h1>
          <p className="text-muted-foreground">
            Everything you might want to know before getting started.
          </p>
        </header>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`}>
              <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </MarketingPage>
  );
}
